/**
 * useAudioSnippetPlayer Hook
 * ORCA360 - Audio Snippet Playback for Speaker Role Assignment
 *
 * Manages a single shared HTMLAudioElement to play time-ranged snippets
 * from a session's audio file via Supabase signed URL.
 */

import { useState, useRef, useEffect, useCallback } from 'react';

export interface AudioPlaybackState {
  isPlaying: boolean;
  currentTime: number; // seconds within the current snippet
  duration: number; // seconds, length of current snippet
  progress: number; // 0-100 percentage
  activeSnippetId: string | null;
  isLoading: boolean;
  error: string | null;
  absoluteTimeMs: number; // current position in audio file (ms)
  snippetStartMs: number; // start of current snippet (ms)
  snippetEndMs: number; // end of current snippet (ms)
}

interface UseAudioSnippetPlayerOptions {
  audioUrl: string | null;
}

const INITIAL_STATE: AudioPlaybackState = {
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  progress: 0,
  activeSnippetId: null,
  isLoading: false,
  error: null,
  absoluteTimeMs: 0,
  snippetStartMs: 0,
  snippetEndMs: 0,
};

export function useAudioSnippetPlayer({ audioUrl }: UseAudioSnippetPlayerOptions) {
  const [state, setState] = useState<AudioPlaybackState>(INITIAL_STATE);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const startSecRef = useRef(0);
  const endSecRef = useRef(0);
  const snippetIdRef = useRef<string | null>(null);
  const rafIdRef = useRef<number | null>(null);

  // Create audio element once on mount
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'metadata';
    audioRef.current = audio;

    const handleWaiting = () => setState((s) => ({ ...s, isLoading: true }));
    const handleCanPlay = () => setState((s) => ({ ...s, isLoading: false }));
    const handlePlaying = () => setState((s) => ({ ...s, isLoading: false }));
    const handleEnded = () => {
      cancelAnimationFrame(rafIdRef.current ?? 0);
      setState((s) => ({ ...s, isPlaying: false, progress: 100, currentTime: s.duration }));
    };
    const handleError = () => {
      cancelAnimationFrame(rafIdRef.current ?? 0);
      const code = audio.error?.code;
      let msg = 'Audio playback error';
      if (code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) msg = 'Audio format not supported';
      else if (code === MediaError.MEDIA_ERR_NETWORK) msg = 'Audio link expired. Please reopen this dialog.';
      else if (code === MediaError.MEDIA_ERR_DECODE) msg = 'Audio decoding error';
      setState((s) => ({ ...s, isPlaying: false, isLoading: false, error: msg }));
    };

    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      cancelAnimationFrame(rafIdRef.current ?? 0);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.pause();
      audio.src = '';
      audio.load();
      audioRef.current = null;
    };
  }, []);

  // Update audio source when URL changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audioUrl) {
      audio.src = audioUrl;
      audio.load();
      setState(INITIAL_STATE);
    }
  }, [audioUrl]);

  // rAF-based progress polling for precise auto-stop
  const startProgressPolling = useCallback(() => {
    const tick = () => {
      const audio = audioRef.current;
      if (!audio || audio.paused) return;

      const current = audio.currentTime;
      const startSec = startSecRef.current;
      const endSec = endSecRef.current;

      if (current >= endSec) {
        audio.pause();
        cancelAnimationFrame(rafIdRef.current ?? 0);
        setState((s) => ({
          ...s,
          isPlaying: false,
          currentTime: endSec - startSec,
          progress: 100,
          absoluteTimeMs: endSec * 1000,
        }));
        return;
      }

      const elapsed = current - startSec;
      const dur = endSec - startSec;
      setState((s) => ({
        ...s,
        currentTime: Math.max(0, elapsed),
        duration: dur,
        progress: dur > 0 ? Math.min(100, (elapsed / dur) * 100) : 0,
        absoluteTimeMs: current * 1000,
      }));

      rafIdRef.current = requestAnimationFrame(tick);
    };
    rafIdRef.current = requestAnimationFrame(tick);
  }, []);

  const playSnippet = useCallback(
    (snippetId: string, startMs: number, endMs: number) => {
      const audio = audioRef.current;
      if (!audio || !audioUrl) return;

      // Stop any current polling
      cancelAnimationFrame(rafIdRef.current ?? 0);

      const startSec = startMs / 1000;
      const endSec = endMs / 1000;

      startSecRef.current = startSec;
      endSecRef.current = endSec;
      snippetIdRef.current = snippetId;

      audio.currentTime = startSec;

      setState({
        isPlaying: true,
        currentTime: 0,
        duration: endSec - startSec,
        progress: 0,
        activeSnippetId: snippetId,
        isLoading: true,
        error: null,
        absoluteTimeMs: startMs,
        snippetStartMs: startMs,
        snippetEndMs: endMs,
      });

      audio.play().then(() => {
        setState((s) => ({ ...s, isLoading: false }));
        startProgressPolling();
      }).catch((err) => {
        console.error('[useAudioSnippetPlayer] Play failed:', err);
        setState((s) => ({
          ...s,
          isPlaying: false,
          isLoading: false,
          error: 'Failed to start playback',
        }));
      });
    },
    [audioUrl, startProgressPolling],
  );

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    cancelAnimationFrame(rafIdRef.current ?? 0);
    audio.pause();
    setState((s) => ({ ...s, isPlaying: false }));
  }, []);

  const resume = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !snippetIdRef.current) return;

    setState((s) => ({ ...s, isPlaying: true }));
    audio.play().then(() => {
      startProgressPolling();
    }).catch(() => {
      setState((s) => ({ ...s, isPlaying: false, error: 'Failed to resume playback' }));
    });
  }, [startProgressPolling]);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    cancelAnimationFrame(rafIdRef.current ?? 0);
    audio.pause();
    snippetIdRef.current = null;
    setState(INITIAL_STATE);
  }, []);

  const seekWithinSnippet = useCallback(
    (fraction: number) => {
      const audio = audioRef.current;
      if (!audio || !snippetIdRef.current) return;

      const startSec = startSecRef.current;
      const endSec = endSecRef.current;
      const dur = endSec - startSec;
      const seekTo = startSec + fraction * dur;

      audio.currentTime = seekTo;
      setState((s) => ({
        ...s,
        currentTime: fraction * dur,
        progress: fraction * 100,
        absoluteTimeMs: seekTo * 1000,
      }));
    },
    [],
  );

  return { state, playSnippet, pause, resume, stop, seekWithinSnippet };
}
